import { createSlice } from "@reduxjs/toolkit";
import {
  getFestivalsBuilder,
  getAdminFestivalsBuilder,
  getFestivalByIdBuilder,
  getFestivalsForDropdownBuilder,
  getFeaturedFestivalsBuilder,
  createFestivalBuilder,
  editFestivalBuilder,
  deleteFestivalBuilder,
  toggleFestivalStatusBuilder,
  getProductsByFestivalBuilder,
  createFestivalDashboardAccessBuilder,
  resetFestivalDashboardPasswordBuilder,
  removeFestivalDashboardAccessBuilder,
  getFestivalDetailsBuilder,
  updateFestivalDetailsBuilder,
  getFestivalPublicDetailsBuilder,
  getFestivalByNameBuilder,
} from "./builder";
import { fetchFestivalAdminCredentials } from "./asyncThunks";

const initialState = {
  festivals: {
    data: [],
    maxReached: false,
    pagination: {
      currentPage: 1,
      hasNextPage: false,
      hasPreviousPage: false,
      limit: 10,
      totalFestivals: 0,
      totalPages: 0,
    },
    loading: false,
    createLoading: false,
    editLoading: false,
    deleteLoading: false,
    toggleLoading: false,
    error: null,
  },

  festivalById: {
    data: null,
    loading: false,
    error: null,
  },

  festivalsDropdown: {
    data: [],
    loading: false,
    error: null,
  },

  featuredFestivals: {
    data: [],
    loading: false,
    error: null,
  },

  dashboardAccess: {
    createLoading: false,
    resetLoading: false,
    removeLoading: false,
    error: null,
    lastCreated: null,
    lastReset: null,
    lastRemoved: null,
  },

  festivalProducts: {
    data: [],
    total: 0,
    page: 1,
    perPage: 12,
    maxReached: false,
    festival: null,
    loading: false,
    error: null,
  },

  adminCredentials: {
    data: {},
    loading: false,
    error: null,
  },

  festivalDetails: {
    data: null,
    loading: false,
    updateLoading: false,
    error: null,
  },

  publicFestivalDetails: {
    data: null,
    loading: false,
    error: null,
  },

  festivalByName: {
    data: null,
    loading: false,
    error: null,
  },
};

export const festivalsSlice = createSlice({
  name: "festivalsInfo",
  initialState,
  reducers: {
    clearFestivalById: (state) => {
      state.festivalById.data = null;
      state.festivalById.error = null;
    },
    clearFestivalsError: (state) => {
      state.festivals.error = null;
    },
    clearFeaturedFestivalsError: (state) => {
      state.featuredFestivals.error = null;
    },
    resetFestivalsState: (state) => {
      state.festivals = initialState.festivals;
      state.festivalById = initialState.festivalById;
      state.festivalsDropdown = initialState.festivalsDropdown;
    },
    clearFestivalProducts: (state) => {
      state.festivalProducts.data = [];
      state.festivalProducts.total = 0;
      state.festivalProducts.page = 1;
      state.festivalProducts.festival = null;
      state.festivalProducts.error = null;
    },

    reset: (state) => {
      state.adminCredentials = {
        data: {},
        loading: false,
        error: null,
      };
    },

    clearDashboardAccessError: (state) => {
      state.dashboardAccess.error = null;
    },
    clearDashboardAccessResults: (state) => {
      state.dashboardAccess.lastCreated = null;
      state.dashboardAccess.lastReset = null;
      state.dashboardAccess.lastRemoved = null;
    },
    resetDashboardAccessState: (state) => {
      state.dashboardAccess = initialState.dashboardAccess;
    },

    clearFestivalDetails: (state) => {
      state.festivalDetails.data = null;
      state.festivalDetails.error = null;
    },

    clearPublicFestivalDetails: (state) => {
      state.publicFestivalDetails.data = null;
      state.publicFestivalDetails.error = null;
    },

    clearFestivalByName: (state) => {
      state.festivalByName.data = null;
      state.festivalByName.error = null;
    },

    resetFestivalDetailsState: (state) => {
      state.festivalDetails = {
        data: null,
        loading: false,
        updateLoading: false,
        error: null,
      };
      state.publicFestivalDetails = {
        data: null,
        loading: false,
        error: null,
      };
      state.festivalByName = {
        data: null,
        loading: false,
        error: null,
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchFestivalAdminCredentials.pending, (state) => {
        state.adminCredentials.loading = true;
      })
      .addCase(fetchFestivalAdminCredentials.fulfilled, (state, action) => {
        state.adminCredentials.loading = false;
        const { festivalId, adminCredentials } = action.payload;
        state.adminCredentials.data[festivalId] = adminCredentials;
      })
      .addCase(fetchFestivalAdminCredentials.rejected, (state) => {
        state.adminCredentials.loading = false;
        state.adminCredentials.error = true;
      });

    getFestivalsBuilder(builder);
    getAdminFestivalsBuilder(builder);
    getFestivalByIdBuilder(builder);
    getFestivalsForDropdownBuilder(builder);
    getFeaturedFestivalsBuilder(builder);
    createFestivalBuilder(builder);
    editFestivalBuilder(builder);
    deleteFestivalBuilder(builder);
    toggleFestivalStatusBuilder(builder);

    getProductsByFestivalBuilder(builder);

    createFestivalDashboardAccessBuilder(builder);
    resetFestivalDashboardPasswordBuilder(builder);
    removeFestivalDashboardAccessBuilder(builder);

    getFestivalDetailsBuilder(builder);
    updateFestivalDetailsBuilder(builder);
    getFestivalPublicDetailsBuilder(builder);
    getFestivalByNameBuilder(builder);

    builder.addCase("auth/logout", (state) => {
      state.festivals = initialState.festivals;
      state.festivalById = initialState.festivalById;
      state.festivalsDropdown = initialState.festivalsDropdown;
      state.dashboardAccess = initialState.dashboardAccess;
      state.festivalProducts = initialState.festivalProducts;
    });
  },
});

export const {
  clearFestivalById,
  clearFestivalsError,
  clearFeaturedFestivalsError,
  resetFestivalsState,
  clearFestivalProducts,
  reset,
  clearDashboardAccessError,
  clearDashboardAccessResults,
  clearFestivalDetails,
  clearPublicFestivalDetails,
  clearFestivalByName,
  resetFestivalDetailsState,
  resetDashboardAccessState,
} = festivalsSlice.actions;

export const selectFestivals = (state) => state.festivalsInfo.festivals;
export const selectFestivalAdminCredentials = (state) =>
  state.festivalsInfo.adminCredentials;
export const selectFestivalById = (state) => state.festivalsInfo.festivalById;
export const selectFestivalsDropdown = (state) =>
  state.festivalsInfo.festivalsDropdown;
export const selectFeaturedFestivals = (state) =>
  state.festivalsInfo.featuredFestivals;
export const selectFestivalProducts = (state) =>
  state.festivalsInfo.festivalProducts;
export const selectFestivalDashboardAccess = (state) =>
  state.festivalsInfo.dashboardAccess;

export const selectActiveFestivals = (state) => ({
  ...state.festivalsInfo.festivals,
  data: state.festivalsInfo.festivals.data.filter((festival) => festival.active),
});

export const selectFeaturedFestivalsFromAll = (state) => ({
  ...state.festivalsInfo.festivals,
  data: state.festivalsInfo.festivals.data.filter(
    (festival) => festival.isFeatured,
  ),
});

export const selectFestivalDetails = (state) =>
  state.festivalsInfo.festivalDetails;
export const selectPublicFestivalDetails = (state) =>
  state.festivalsInfo.publicFestivalDetails;
export const selectFestivalByName = (state) =>
  state.festivalsInfo.festivalByName;

export default festivalsSlice.reducer;
